import React, { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ChevronDown, X } from 'lucide-react';

function ContentItemModal({
  isOpen,
  onClose,
  onSave,
  title,
  creators = [],
  contentItem,
  platformOptions = ['facebook', 'instagram', 'youtube', 'linkedin'],
  multiPlatform = false
}) {
  const [date, setDate] = useState('');
  const [description, setDescription] = useState('');
  const [contentTitle, setContentTitle] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState(
    Array.isArray(contentItem?.type)
      ? contentItem.type
      : contentItem?.type
        ? [contentItem.type]
        : []
  );
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('High');
  const [assignedTo, setAssignedTo] = useState('');
  const [postType, setPostType] = useState('post');
  const [errors, setErrors] = useState({});
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef();
  const modalRef = useRef(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (contentItem) {
      setDate(contentItem.date || '');
      setDescription(contentItem.description || '');
      setContentTitle(contentItem.title || '');
      setSelectedPlatforms(
        Array.isArray(contentItem.type)
          ? contentItem.type
          : contentItem.type
            ? [contentItem.type]
            : []
      );
      setDueDate(contentItem.dueDate || '');
      setPriority(contentItem.priority || 'High');
      setAssignedTo(contentItem.assignedTo || '');
      setPostType(contentItem.postType || 'post');
    } else {
      setDate('');
      setDueDate('');
      setDescription('');
      setContentTitle('');
      setSelectedPlatforms([]);
      setPriority('High');
      setAssignedTo('');
      setPostType('post');
    }
  }, [contentItem, isOpen]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  const availablePostTypes = [
    { value: 'post', label: 'Post' },
    { value: 'reel', label: 'Reel/Video' },
    { value: 'carousel', label: 'Carousel' },
    { value: 'story', label: 'Story' }
  ];

  const platformCapabilities = {
    facebook: ['post', 'reel', 'carousel', 'story'],
    instagram: ['post', 'reel', 'carousel', 'story'],
    linkedin: ['post', 'carousel', 'reel'], // 'reel' maps to video for LinkedIn
    youtube: ['reel'] // YouTube only supports reels/videos
  };

  const getValidPostTypes = () => {
    if (selectedPlatforms.length === 0) return availablePostTypes;
    
    let validTypes = platformCapabilities[selectedPlatforms[0]] || availablePostTypes.map(pt => pt.value);
    
    for (let i = 1; i < selectedPlatforms.length; i++) {
      const caps = platformCapabilities[selectedPlatforms[i]] || availablePostTypes.map(pt => pt.value);
      validTypes = validTypes.filter(t => caps.includes(t));
    }
    
    return availablePostTypes.filter(pt => validTypes.includes(pt.value));
  };

  const validPostTypes = getValidPostTypes();

  useEffect(() => {
    if (validPostTypes.length > 0 && !validPostTypes.find(pt => pt.value === postType)) {
      setPostType(validPostTypes[0].value);
    }
  }, [selectedPlatforms]); // Recalculate post type when selectedPlatforms changes

  const validate = () => {
    const newErrors = {};
    if (!description.trim()) newErrors.description = 'Content description is required';
    if (!contentTitle.trim()) newErrors.contentTitle = 'Title is required';
    if (!dueDate) newErrors.dueDate = 'Due date is required';
    if (!assignedTo) newErrors.assignedTo = 'Please select a content creator';
    if (validPostTypes.length === 0) newErrors.postType = 'No common post types available for selected platforms';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave({
        ...(contentItem || {}),
        id: contentItem?.id || uuidv4(),
        date: dueDate, // Use dueDate for the date field
        description,
        title: contentTitle,
        type: multiPlatform ? selectedPlatforms : selectedPlatforms[0],
        postType,
        dueDate,
        priority,
        assignedTo,
        status: contentItem?.status || 'pending', // Set initial status to 'pending'
        originalDate: contentItem?.originalDate || contentItem?.date,
        originalDescription: contentItem?.originalDescription || contentItem?.description
      });
      onClose();
    }
  };

  const handleCheckboxChange = (platform) => {
    setSelectedPlatforms(prev =>
      prev.includes(platform)
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black bg-opacity-50 animate-fade-in">
      <div 
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl w-full max-w-xs sm:max-w-md mx-auto animate-slide-up"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-medium">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6">
          <div className="max-h-[80vh] overflow-y-auto px-4 py-6" style={{ minHeight: '0' }}>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Title</label>
                <input
                  type="text"
                  className="input-field"
                  value={contentTitle}
                  onChange={(e) => setContentTitle(e.target.value)}
                />
                {errors.contentTitle && <p className="text-sm text-red-600">{errors.contentTitle}</p>}
              </div>

              <div className="mb-4" ref={dropdownRef}>
                <label className="block text-sm font-medium mb-1">Type</label>
                <div className="relative">
                  <button
                    type="button"
                    className={`form-select w-full text-left flex items-center justify-between bg-white border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 hover:border-blue-400 transition`}
                    onClick={() => setDropdownOpen((open) => !open)}
                    tabIndex={0}
                  >
                    <div className="flex flex-wrap gap-1">
                      {selectedPlatforms.length === 0
                        ? <span className="text-gray-400">Select platforms</span>
                        : selectedPlatforms.map(p => (
                            <span
                              key={p}
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 mr-1`}
                            >
                              {p.charAt(0).toUpperCase() + p.slice(1)}
                            </span>
                          ))
                      }
                    </div>
                    <ChevronDown className="h-4 w-4 text-gray-500 ml-2" />
                  </button>
                  {dropdownOpen && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg max-h-48 overflow-y-auto animate-fade-in">
                      {platformOptions.map(opt => (
                        <label key={opt} className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer transition">
                          <input
                            type="checkbox"
                            checked={selectedPlatforms.includes(opt)}
                            onChange={() => handleCheckboxChange(opt)}
                            className="mr-2 accent-blue-600"
                          />
                          <span className="text-gray-700">{opt.charAt(0).toUpperCase() + opt.slice(1)}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <span className="text-xs text-gray-500">Select one or more platforms.</span>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Post Type</label>
                <select
                  className="input-field"
                  value={postType}
                  onChange={(e) => setPostType(e.target.value)}
                  disabled={validPostTypes.length === 0}
                >
                  {validPostTypes.length > 0 ? (
                    validPostTypes.map(pt => (
                      <option key={pt.value} value={pt.value}>{pt.label}</option>
                    ))
                  ) : (
                    <option value="">No compatible type</option>
                  )}
                </select>
                {errors.postType && <p className="text-sm text-red-600 mt-1">{errors.postType}</p>}
                {validPostTypes.length > 0 && selectedPlatforms.length > 1 && (
                  <p className="text-xs text-gray-500 mt-1">Showing post types supported by all selected platforms.</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Due Date</label>
                <input
                  type="date"
                  className="input-field"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
                {errors.dueDate && <p className="text-sm text-red-600">{errors.dueDate}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Priority</label>
                <select className="input-field" value={priority} onChange={(e) => setPriority(e.target.value)}>
                  <option>High</option>
                  <option>Normal</option>
                  <option>Low</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Assign To (Content Creator)</label>
                <select
                  className="input-field"
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value)}
                >
                  <option value="">-- Select Creator --</option>
                  {creators.map(creator => (
                    <option key={creator._id} value={creator.email}>
                      {creator.email}
                    </option>
                  ))}
                </select>
                {errors.assignedTo && <p className="text-sm text-red-600">{errors.assignedTo}</p>}
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-1">Content Description</label>
                <textarea
                  rows={3}
                  className="input-field"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter content description"
                />
                {errors.description && <p className="text-sm text-red-600">{errors.description}</p>}
              </div>

              <div className="flex justify-end space-x-3">
                <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn-primary">{contentItem ? 'Update' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContentItemModal;
